import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox,
  Chip, Alert, CircularProgress, Snackbar, Divider, Tooltip, LinearProgress,
} from '@mui/material';
import { Lock as LockIcon, Save as SaveIcon } from '@mui/icons-material';
import { accessControlService } from '../services/api';

const ACTIONS = [
  ['view', 'View'],
  ['create', 'Create'],
  ['edit', 'Edit'],
  ['delete', 'Delete'],
];

const AccessControl = () => {
  const [data, setData] = useState(null); // { roles, modules, matrix }
  const [work, setWork] = useState({});   // work[moduleKey][role] = {view,create,edit,delete}
  const [role, setRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });

  const showToast = (msg, severity = 'success') => setToast({ open: true, msg, severity });

  const load = async () => {
    setLoading(true);
    try {
      const res = await accessControlService.getMatrix();
      const d = res.data.data;
      // Build a fully-populated working copy (default false where no row exists)
      const w = {};
      d.modules.forEach((m) => {
        w[m.key] = {};
        d.roles.forEach((r) => {
          const cell = d.matrix?.[m.key]?.[r] || {};
          w[m.key][r] = {
            view: !!cell.view, create: !!cell.create, edit: !!cell.edit, delete: !!cell.delete,
          };
        });
      });
      setData(d);
      setWork(w);
      setDirty(false);
      // default to first non-owner role
      const firstRole = d.roles.find((r) => r !== 'owner') || d.roles[0];
      setRole(firstRole);
    } catch (e) {
      showToast(e.response?.data?.message || 'Failed to load access matrix', 'error');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const isOwnerRole = role === 'owner';

  const setCell = (moduleKey, action, val) => {
    setWork((prev) => {
      const cell = { ...prev[moduleKey][role], [action]: val };
      // Enabling a write action implies View; removing View removes the rest.
      if (val && action !== 'view') cell.view = true;
      if (!val && action === 'view') { cell.create = false; cell.edit = false; cell.delete = false; }
      return { ...prev, [moduleKey]: { ...prev[moduleKey], [role]: cell } };
    });
    setDirty(true);
  };

  const setModuleAll = (moduleKey, val) => {
    setWork((prev) => ({
      ...prev,
      [moduleKey]: { ...prev[moduleKey], [role]: { view: val, create: val, edit: val, delete: val } },
    }));
    setDirty(true);
  };

  const setColumnAll = (action, val) => {
    setWork((prev) => {
      const next = { ...prev };
      (data?.modules || []).forEach((m) => {
        const cell = { ...next[m.key][role], [action]: val };
        if (val && action !== 'view') cell.view = true;
        if (!val && action === 'view') { cell.create = false; cell.edit = false; cell.delete = false; }
        next[m.key] = { ...next[m.key], [role]: cell };
      });
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const changes = (data?.modules || []).map((m) => ({
        role, moduleKey: m.key, ...work[m.key][role],
        canView: work[m.key][role].view, canCreate: work[m.key][role].create,
        canEdit: work[m.key][role].edit, canDelete: work[m.key][role].delete,
      }));
      await accessControlService.updateMatrix(changes);
      showToast(`Access saved for "${role}"`);
      setDirty(false);
    } catch (e) {
      showToast(e.response?.data?.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const sections = useMemo(() => {
    const order = [];
    const map = {};
    (data?.modules || []).forEach((m) => {
      if (!map[m.section]) { map[m.section] = []; order.push(m.section); }
      map[m.section].push(m);
    });
    return order.map((s) => ({ section: s, modules: map[s] }));
  }, [data]);

  if (loading) {
    return <Box sx={{ p: 3 }}><LinearProgress /><Typography sx={{ mt: 2 }} color="text.secondary">Loading access matrix…</Typography></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Access Control</Typography>
          <Typography variant="body2" color="text.secondary">
            Toggle what each role can View / Create / Edit / Delete, per module. Changes apply immediately after saving.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />} disabled={saving || !dirty || isOwnerRole} onClick={save}>
          Save Changes
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Owner</strong> always has full access and cannot be restricted. Enabling Create/Edit/Delete automatically grants View.
      </Alert>

      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Role</InputLabel>
          <Select label="Role" value={role} onChange={(e) => setRole(e.target.value)}>
            {(data?.roles || []).map((r) => (
              <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>
                {r}{r === 'owner' ? ' (always full access)' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {dirty && !isOwnerRole && <Chip color="warning" size="small" label="Unsaved changes" />}
        {!isOwnerRole && (
          <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', flexWrap: 'wrap' }}>
            {ACTIONS.map(([a, label]) => (
              <Button key={a} size="small" variant="text" onClick={() => setColumnAll(a, true)}>All {label}</Button>
            ))}
            <Button size="small" color="error" variant="text" onClick={() => ACTIONS.forEach(([a]) => setColumnAll(a, false))}>Clear all</Button>
          </Box>
        )}
      </Paper>

      {isOwnerRole ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <LockIcon color="disabled" sx={{ fontSize: 40 }} />
          <Typography sx={{ mt: 1 }} color="text.secondary">The Owner role always has full access to every module and cannot be modified.</Typography>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'action.hover' } }}>
                  <TableCell sx={{ minWidth: 200 }}>Module</TableCell>
                  {ACTIONS.map(([a, label]) => <TableCell key={a} align="center">{label}</TableCell>)}
                  <TableCell align="center">All</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sections.map(({ section, modules }) => (
                  <React.Fragment key={section}>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ bgcolor: 'action.selected', py: 0.5 }}>
                        <Typography variant="caption" fontWeight={700} color="primary">{section}</Typography>
                      </TableCell>
                    </TableRow>
                    {modules.map((m) => {
                      const cell = work[m.key]?.[role] || {};
                      const allOn = cell.view && cell.create && cell.edit && cell.delete;
                      return (
                        <TableRow key={m.key} hover>
                          <TableCell>
                            <Typography variant="body2">{m.label}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{m.key}</Typography>
                          </TableCell>
                          {ACTIONS.map(([a]) => (
                            <TableCell key={a} align="center">
                              <Checkbox size="small" checked={!!cell[a]} onChange={(e) => setCell(m.key, a, e.target.checked)} />
                            </TableCell>
                          ))}
                          <TableCell align="center">
                            <Tooltip title={allOn ? 'Clear all' : 'Grant all'}>
                              <Checkbox size="small" indeterminate={!allOn && (cell.view || cell.create || cell.edit || cell.delete)} checked={allOn} onChange={(e) => setModuleAll(m.key, e.target.checked)} />
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />} disabled={saving || !dirty} onClick={save}>
              Save Changes
            </Button>
          </Box>
        </Paper>
      )}

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast((t) => ({ ...t, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AccessControl;
